import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";

import {
  VALUE_LIST_LIMIT,
  applySelection,
  defaultCellText,
  defaultSortKey,
  pickFilters,
  rowsPassing,
  valueCounts,
} from "../utils/column_value_filter";

// Аналитический фильтр «⏷» у заголовка столбца: список значений со
// счётчиками и долей, сортировка А-Я / по числу строк, «Выбрать все» /
// «Снять все». Впервые сделан в «Просроченной ДЗ»; заказчик попросил тот же
// в «Счетах», «Потенциале» и «Открытой прибыли» (21.10.2026).
//
// ``rows`` — строки отчёта до отборов по столбцам. Счётчики считаются только
// когда список открыт (isLazy), поэтому кнопки в шапке таблицу не тормозят.

const smallButton = { size: "xs", bg: "#444", color: "white", _hover: { bg: "#555" }, flex: 1 };

const FilterBody = ({ columnKey, rows, filters, onChange, textOf, sortKeyOf }) => {
  const [mode, setMode] = useState("alpha");
  const [query, setQuery] = useState("");

  const base = useMemo(
    () => rowsPassing(rows, filters, textOf, columnKey),
    [rows, filters, textOf, columnKey]
  );
  const values = useMemo(
    () => valueCounts(base, columnKey, mode, textOf, sortKeyOf),
    [base, columnKey, mode, textOf, sortKeyOf]
  );

  const allNames = values.map((v) => v.name);
  const selected = filters[columnKey] || new Set(allNames);
  const needle = query.trim().toLowerCase();
  const matched = needle ? values.filter((v) => (v.name || "(пусто)").toLowerCase().includes(needle)) : values;
  const shown = matched.slice(0, VALUE_LIST_LIMIT);

  const apply = (next) => onChange(applySelection(filters, columnKey, allNames, next));
  const toggle = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    apply(next);
  };
  // С поиском «Выбрать все» / «Снять все» действуют на найденные значения.
  const selectAll = () => {
    if (!needle) return apply(new Set(allNames));
    const next = new Set(selected);
    matched.forEach((v) => next.add(v.name));
    return apply(next);
  };
  const clearAll = () => {
    if (!needle) return apply(new Set());
    const next = new Set(selected);
    matched.forEach((v) => next.delete(v.name));
    return apply(next);
  };
  const matchedRows = matched.reduce((sum, v) => sum + v.count, 0);

  return (
    <>
      <HStack mb={2}>
        <Button {...smallButton} onClick={() => setMode("alpha")}>А-Я</Button>
        <Button {...smallButton} onClick={() => setMode("desc")}>% ↓</Button>
        <Button {...smallButton} onClick={() => setMode("asc")}>% ↑</Button>
      </HStack>
      <HStack mb={2}>
        <Button {...smallButton} onClick={selectAll}>
          Выбрать все ({needle ? matchedRows : base.length})
        </Button>
        <Button {...smallButton} onClick={clearAll}>Снять все</Button>
      </HStack>
      {values.length > 10 && (
        <Input
          size="xs"
          mb={3}
          placeholder="Поиск значения"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          bg="#333"
          borderColor="#555"
        />
      )}
      <VStack align="stretch" spacing={1}>
        {shown.map((v) => (
          <HStack key={v.name} justify="space-between">
            <Checkbox colorScheme="green" isChecked={selected.has(v.name)} onChange={() => toggle(v.name)}>
              <Text fontSize="sm">{v.name || "(пусто)"}</Text>
            </Checkbox>
            <Text fontSize="sm" color="gray.400" whiteSpace="nowrap">
              {v.count} ({base.length ? ((v.count / base.length) * 100).toFixed(1) : "0.0"}%)
            </Text>
          </HStack>
        ))}
        {matched.length > shown.length && (
          <Text fontSize="xs" color="gray.400" pt={1}>
            Показаны первые {shown.length} из {matched.length} значений — уточните поиском.
          </Text>
        )}
      </VStack>
    </>
  );
};

const ColumnValueFilter = ({
  columnKey,
  title,
  rows,
  filters,
  onChange,
  textOf = defaultCellText,
  sortKeyOf = defaultSortKey,
}) => {
  const active = Boolean(filters[columnKey]);
  return (
    <Popover placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Button
          size="xs"
          minW="22px"
          h="18px"
          px={0}
          flexShrink={0}
          bg={active ? "green.400" : "#555"}
          color="white"
          _hover={{ bg: active ? "green.500" : "#666" }}
          onClick={(e) => e.stopPropagation()}
          title={`Аналитический фильтр: ${title}`}
        >
          ⏷
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="#222"
          color="white"
          borderColor="#555"
          w="400px"
          zIndex={1500}
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverBody maxH="440px" overflowY="auto">
            <FilterBody
              columnKey={columnKey}
              rows={rows}
              filters={filters}
              onChange={onChange}
              textOf={textOf}
              sortKeyOf={sortKeyOf}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

// Заголовок столбца с «⏷»: кнопка стоит сразу за заголовком, в той же строке,
// если хватает места, иначе — на следующей строке под ним. Прижатая к
// правому краю колонки, она оказывалась вплотную к заголовку соседней
// колонки и читалась как её (замечание 21.10.2026). Склеивать кнопку со
// словом неразрывно нельзя: в узкой колонке её обрезало («Организация»).
export const HeaderTitle = ({ title, suffix = null, filter = null }) => (
  <>
    {title}
    {suffix}
    {filter && (
      <>
        {" "}
        <Box as="span" display="inline-block" verticalAlign="middle" lineHeight={0}>
          {filter}
        </Box>
      </>
    )}
  </>
);

// Отборы по столбцам отчёта: сбрасываются при новой выборке (к ней прежние
// значения не относятся) и учитываются только у показанных столбцов.
export const useColumnFilters = (data, columnKeys) => {
  const [filters, setFilters] = useState({});
  const [dataRef, setDataRef] = useState(data);
  if (dataRef !== data) {
    setDataRef(data);
    setFilters({});
  }
  const keysSignature = (columnKeys || []).join("|");
  const effective = useMemo(
    () => pickFilters(filters, columnKeys || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, keysSignature]
  );
  return [effective, setFilters];
};

export const ResetColumnFiltersButton = ({ filters, onReset }) =>
  Object.keys(filters).length > 0 ? (
    <Button size="sm" variant="outline" color="white" borderColor="#555" _hover={{ bg: "#333" }} onClick={onReset}>
      Сбросить фильтры столбцов
    </Button>
  ) : null;

export default ColumnValueFilter;
